import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  Button,
  useTheme,
} from '@mui/material';
import {
  Storage as DatabaseIcon,
  HealthAndSafety as HealthIcon,
  Refresh as RefreshIcon,
  TableChart as TableIcon,
  Launch as LaunchIcon,
  Apps as AppsIcon,
  Info as InfoIcon,
  PhotoCamera as PhotoCameraIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getDynamicMenuItems, Application, FormTable } from '../services/menuService';
import api from '../services/api';
import { config } from '../config/environment';
import { VERSION } from '../config/version';
import ListView from '../components/ListView';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`system-tabpanel-${index}`}
      aria-labelledby={`system-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Use FormTable from menuService as our SystemTable interface since they're the same
type SystemTable = FormTable;

interface SystemHealth {
  status: 'healthy' | 'degraded' | 'down';
  version: string;
  environment: string;
  uptime: number;
  database: {
    connected: boolean;
    version?: string;
    type?: string;
  };
  memory?: {
    usage: number;
    total: number;
    unit: string;
  };
  timestamp: string;
}

interface BackendInfo {
  environment: string;
  version: string;
  dbConnected: boolean;
  apiStatus: 'healthy' | 'degraded' | 'down';
  systemHealth?: SystemHealth;
  buildTime?: string;
}

const SystemDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [systemTables, setSystemTables] = useState<SystemTable[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [backendInfo, setBackendInfo] = useState<BackendInfo>({
    environment: 'Unknown',
    version: 'Unknown',
    dbConnected: false,
    apiStatus: 'down'
  });

  // Get frontend version from package.json
  const frontendVersion = VERSION;

  useEffect(() => {
    loadSystemData();
    fetchBackendInfo();
  }, []);

  const loadSystemData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use the existing menuService function that correctly calls the real APIs
      const menuData = await getDynamicMenuItems();
      
      // Convert FormTable to SystemTable format (they're the same now)
      const allTables = Object.values(menuData.tablesByApp).flat();
      setSystemTables(allTables);
      
      // Use real applications data
      setApplications(menuData.applications);

    } catch (err: any) {
      setError('Unable to load system data. Using basic configuration.');
      console.error('System data loading error:', err);
      // Set some basic fallback data
      setSystemTables([
        { 
          id: 1, 
          table_name: 'users', 
          label: 'Users', 
          description: 'System users', 
          application_id: 1,
          visible: true,
          sort_order: 1
        },
        { 
          id: 2, 
          table_name: 'applications', 
          label: 'Applications', 
          description: 'Application definitions', 
          application_id: 1,
          visible: true,
          sort_order: 2
        }
      ]);
      setApplications([
        { 
          id: 1, 
          name: 'system', 
          display_name: 'System', 
          description: 'System configuration', 
          icon: 'settings',
          sort_order: 0
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBackendInfo = async () => {
    try {
      const response = await api.get('/system/health');
      if (response.data) {
        setBackendInfo({
          environment: response.data.environment || 'Unknown',
          version: response.data.version || 'Unknown',
          dbConnected: response.data.database?.connected || false,
          apiStatus: response.data.status || 'degraded',
          systemHealth: response.data,
          buildTime: response.data.buildTime
        });
      }
    } catch (err) {
      console.error('Error fetching backend info:', err);
      // Keep default values if fetch fails
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleTableClick = (tableName: string) => {
    navigate(`/${tableName}`);
  };

  const renderStatCard = (title: string, value: string | number, icon: React.ReactElement, color: string, subtitle?: string) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h4" sx={{ color, fontWeight: 'bold' }}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ color, opacity: 0.7 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const formatUptime = (seconds: number): string => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h4" component="h1">
            System Dashboard
          </Typography>
          <Chip 
            label={`v${frontendVersion}`} 
            color="primary" 
            size="small" 
            variant="outlined" 
          />
        </Box>
        <Box>
          <Tooltip title="Refresh Data">
            <IconButton onClick={() => {
              loadSystemData();
              fetchBackendInfo();
            }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Overview" />
          <Tab label="System Tables" />
          <Tab label="Applications" />
          <Tab label="Settings" />
        </Tabs>
      </Paper>

      {/* Overview Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {/* System Stats */}
          <Grid item xs={12} sm={6} md={3}>
            {renderStatCard(
              'System Tables', 
              systemTables.length, 
              <TableIcon fontSize="large" />, 
              theme.palette.primary.main
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {renderStatCard(
              'Applications', 
              applications.length, 
              <AppsIcon fontSize="large" />, 
              theme.palette.success.main
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {renderStatCard(
              'System Status', 
              backendInfo.apiStatus === 'healthy' ? 'Healthy' : 
              backendInfo.apiStatus === 'degraded' ? 'Degraded' : 'Down', 
              <HealthIcon fontSize="large" />, 
              backendInfo.apiStatus === 'healthy' ? theme.palette.success.main :
              backendInfo.apiStatus === 'degraded' ? theme.palette.warning.main : theme.palette.error.main,
              backendInfo.systemHealth ? 
                `Uptime: ${formatUptime(backendInfo.systemHealth.uptime)}` : undefined
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {renderStatCard(
              'Database', 
              backendInfo.dbConnected ? 'Connected' : 'Disconnected',
              <DatabaseIcon fontSize="large" />, 
              backendInfo.dbConnected ? theme.palette.success.main : theme.palette.error.main
            )}
          </Grid>

          {/* Quick Access */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Access to System Tables
                </Typography>
                <Grid container spacing={2} mt={1}>
                  {systemTables.slice(0, 6).map((table) => (
                    <Grid item xs={12} sm={6} md={4} key={table.table_name}>
                      <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<TableIcon />}
                        onClick={() => handleTableClick(table.table_name)}
                        sx={{ 
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          py: 1.5
                        }}
                      >
                        <Box textAlign="left">
                          <Typography variant="body2" fontWeight="bold">
                            {table.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {table.table_name}
                          </Typography>
                        </Box>
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* System Tools */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  System Tools
                </Typography>
                <Box mt={2}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<PhotoCameraIcon />}
                    onClick={() => navigate('/s3-images')}
                    sx={{ 
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      py: 1.5,
                      mb: 1
                    }}
                  >
                    <Box textAlign="left">
                      <Typography variant="body2" fontWeight="bold">
                        Image Manager
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Browse and manage uploaded images
                      </Typography>
                    </Box>
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Version Information */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <InfoIcon color="primary" />
                  <Typography variant="h6">
                    Version Information
                  </Typography>
                </Box>
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    System Version
                  </Typography>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">
                      Version:
                    </Typography>
                    <Chip 
                      label={`v${frontendVersion}`} 
                      color="primary" 
                      size="small"
                      variant="filled"
                    />
                  </Box>
                  <Typography variant="body2" gutterBottom>
                    <strong>Build Date:</strong> {backendInfo.buildTime ? 
                      new Date(backendInfo.buildTime).toLocaleDateString() + ' ' + 
                      new Date(backendInfo.buildTime).toLocaleTimeString() : 'Unknown'}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1.5 }} />
                
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  System Status
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Environment:</strong> {backendInfo.environment}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Status:</strong>{' '}
                  <Chip 
                    size="small"
                    label={backendInfo.apiStatus}
                    color={backendInfo.apiStatus === 'healthy' ? 'success' : 
                           backendInfo.apiStatus === 'degraded' ? 'warning' : 'error'}
                  />
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Database:</strong> {backendInfo.dbConnected ? 'Connected' : 'Disconnected'}
                </Typography>
                
                {backendInfo.systemHealth && (
                  <>
                    <Typography variant="body2" gutterBottom>
                      <strong>Uptime:</strong> {formatUptime(backendInfo.systemHealth.uptime)}
                    </Typography>
                  </>
                )}
                
                <Divider sx={{ my: 1.5 }} />
                
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  System Statistics
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>System Tables:</strong> {systemTables.length}
                </Typography>
                <Typography variant="body2">
                  <strong>Applications:</strong> {applications.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* System Tables Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            System Tables
          </Typography>
          <ListView tableName="form_tables" />
        </Box>
      </TabPanel>

      {/* Applications Tab */}
      <TabPanel value={tabValue} index={2}>
        {applications.length > 0 ? (
          <Grid container spacing={3}>
            {applications.map((app) => (
              <Grid item xs={12} sm={6} md={4} key={app.id}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {app.display_name || app.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {app.description}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Sort Order: {app.sort_order}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Applications
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No applications configured yet.
              </Typography>
            </CardContent>
          </Card>
        )}
      </TabPanel>

      {/* Settings Tab */}
      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Version Information
                </Typography>
                <Box mt={2}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Application Version
                  </Typography>
                  
                  <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <Typography variant="body2" sx={{ minWidth: 100 }}>
                      <strong>System Version:</strong>
                    </Typography>
                    <Chip label={`v${frontendVersion}`} color="primary" size="small" />
                  </Box>
                  
                  <Typography variant="body2" gutterBottom>
                    <strong>Build Date:</strong> {backendInfo.buildTime ? 
                      new Date(backendInfo.buildTime).toLocaleDateString() + ' ' + 
                      new Date(backendInfo.buildTime).toLocaleTimeString() : 'Unknown'}
                  </Typography>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Frontend Environment
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Environment:</strong> {config.environment}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>API URL:</strong> {config.apiUrl}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Debug Mode:</strong> {config.debug ? 'Enabled' : 'Disabled'}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>AWS Region:</strong> {config.aws.region}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Frontend Bucket:</strong> {config.aws.frontendBucket}
                  </Typography>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Backend Status
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Environment:</strong> {backendInfo.environment}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Status:</strong>{' '}
                    <Chip 
                      size="small"
                      label={backendInfo.apiStatus}
                      color={backendInfo.apiStatus === 'healthy' ? 'success' : 
                             backendInfo.apiStatus === 'degraded' ? 'warning' : 'error'}
                    />
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Database:</strong> {backendInfo.dbConnected ? 'Connected' : 'Disconnected'}
                  </Typography>
                  
                  {backendInfo.systemHealth && (
                    <>
                      <Typography variant="body2" gutterBottom>
                        <strong>Uptime:</strong> {formatUptime(backendInfo.systemHealth.uptime)}
                      </Typography>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Actions
                </Typography>
                <Box mt={2}>
                  <Button 
                    variant="outlined" 
                    fullWidth
                    sx={{ mb: 2 }}
                    onClick={() => setTabValue(1)}
                  >
                    View System Tables
                  </Button>
                  <Button 
                    variant="outlined" 
                    fullWidth
                    sx={{ mb: 2 }}
                    onClick={() => setTabValue(2)}
                  >
                    View Applications
                  </Button>
                  <Button 
                    variant="outlined" 
                    fullWidth
                    onClick={() => {
                      loadSystemData();
                      fetchBackendInfo();
                    }}
                  >
                    Refresh All Data
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
    </Container>
  );
};

export default SystemDashboard; 